// Components
import CheckableDateRange from 'components/CheckableDateRange/CheckableDateRange';
import RelativeTime from 'components/RelativeTime/RelativeTime';
import dayjs from 'dayjs';
import type { IRangeValue } from 'types/dateTime.type';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Types
import type { IProps } from './sections.interface';

const ReservationRange = ({ timeRule, onChange }: IProps) => {
  const dateRange: IRangeValue = [
    !timeRule.absoluteStartTime ? null : dayjs.unix(timeRule.absoluteStartTime),
    !timeRule.absoluteEndTime
      ? null
      : dayjs.unix(timeRule.absoluteEndTime).subtract(1, 'day'),
  ];
  const cancellationDateRange: IRangeValue = [
    !timeRule.absoluteCancellationStartTime
      ? null
      : dayjs.unix(timeRule.absoluteCancellationStartTime),
    !timeRule.absoluteCancellationEndTime
      ? null
      : dayjs.unix(timeRule.absoluteCancellationEndTime).subtract(1, 'day'),
  ];
  const handleDateRangeChange = (value: IRangeValue) => {
    if (!value) {
      onChange({ object: { absoluteStartTime: 0, absoluteEndTime: 0 } });
      return;
    }
    onChange({
      object: {
        absoluteStartTime: getUnixTime(value[0]),
        absoluteEndTime: getUnixTime(value[1]?.add(1, 'day')),
      },
    });
  };
  const handleCancellationDateRangeChange = (value: IRangeValue) => {
    if (!value) {
      onChange({
        object: {
          absoluteCancellationStartTime: 0,
          absoluteCancellationEndTime: 0,
        },
      });
      return;
    }
    onChange({
      object: {
        absoluteCancellationStartTime: getUnixTime(value[0]),
        absoluteCancellationEndTime: getUnixTime(value[1]?.add(1, 'day')),
      },
    });
  };
  return (
    <TEDrawerSection label="Reservation range">
      <div className="section-wrapper">
        <Space direction="vertical">
          <CheckableDateRange
            label="Use defined date range"
            tooltip="Use defined date range"
            value={dateRange}
            onChange={handleDateRangeChange}
          />
          <RelativeTime
            label="Start offset"
            tooltip="Start offset"
            value={timeRule.relativeStartTime}
            onChange={(value) => onChange({ key: 'relativeStartTime', value })}
            dataTestId="date-range-start"
          />
          <RelativeTime
            label="End offset"
            tooltip="End offset"
            value={timeRule.relativeEndTime}
            onChange={(value) => onChange({ key: 'relativeEndTime', value })}
            dataTestId="date-range-end"
          />
          <TEInput.Switch
            label="Open at start of day"
            tooltip="Open at start of day"
            checked={timeRule.useStartTimeToOpenNextDay}
            onChange={(value) =>
              onChange({ key: 'useStartTimeToOpenNextDay', value })
            }
            data-testid="open-start-day"
          />
        </Space>
        <Space direction="vertical">
          <TEInput.Switch
            label="Separate cancellation rules"
            tooltip="Separate cancellation rules"
            checked={timeRule.useSeparateCancellationRules}
            onChange={(value) =>
              onChange({ key: 'useSeparateCancellationRules', value })
            }
            data-testid="separate-cancellation"
          />
          <CheckableDateRange
            label="Use defined date range"
            tooltip="Use defined date range"
            value={cancellationDateRange}
            onChange={handleCancellationDateRangeChange}
            disabled={!timeRule.useSeparateCancellationRules}
            dataTestId="cancel-date-range"
          />
          <RelativeTime
            label="Start offset"
            tooltip="Start offset"
            value={timeRule.relativeCancellationStartTime}
            onChange={(value) =>
              onChange({ key: 'relativeCancellationStartTime', value })
            }
            disabled={!timeRule.useSeparateCancellationRules}
            dataTestId="cancel-date-range-start"
          />
          <RelativeTime
            label="End offset"
            tooltip="End offset"
            value={timeRule.relativeCancellationEndTime}
            onChange={(value) =>
              onChange({ key: 'relativeCancellationEndTime', value })
            }
            disabled={!timeRule.useSeparateCancellationRules}
            dataTestId="cancel-date-range-end"
          />
        </Space>
      </div>
    </TEDrawerSection>
  );
};

export default ReservationRange;

const getUnixTime = (date?: dayjs.Dayjs) =>
  date ? dayjs.utc(date.format('YYYY-MM-DD')).unix() : 0;
